/**
 * Typed(-ish) API client for Professional Contacts / Professional Details.
 *
 * The ONLY module in the feature that calls fetch/axios (golden rule #2) — every screen goes
 * through here, so the base URL can move from the Prism mock to a real API with one env var.
 *
 * Reuses the app's existing httpGet/httpPost/httpPut/httpPatch/httpDelete (Utils/apis/apis.js)
 * rather than a new axios instance: those helpers use the request as-is whenever it's already
 * an absolute URL, so pointing this client at a different host (the Prism mock) doesn't disturb
 * the shared `API_URL` every other feature in the app uses.
 *
 * @see openapi/professional-contacts-openapi.json (v1.2.0) — the contract is law (golden rule #1).
 */
import { httpGet, httpPost, httpPut, httpPatch, httpDelete } from "./apis";
// Reused rather than duplicated (open decision #6, confirmed: match existing precedent) — this
// is the same BankFinder Azure Function call AddPaymentDetails.helper.js already makes directly
// from the browser. See lookupSortCode below.
import { lookupBankBySortCode } from "../../components/Factory/Molecules/Common/customForms/AddCustomCards/Add_Payment_Details/AddPaymentDetails.helper";
// Same precedent as lookupSortCode above: reused directly rather than re-implemented. These
// already speak the app's one `api/document_helper` Azure Function contract (Content-Type,
// file-operation/process-file/x-functions-key/subdomain headers) that every other Loan Document
// upload/download in this app goes through — see uploadQuoteDocument/downloadQuoteDocument below.
import {
  uploadDocument as uploadToDocumentHelper,
  downloadDocument as downloadFromDocumentHelper,
  deleteDocument as deleteFromDocumentHelper,
} from "../../components/Factory/Pages/Common/DocumentsDashboard/DocumentsDashboard.helper";
import {
  QUOTE_DOCUMENT_CATEGORY_NAME,
  buildQuoteDocumentFilePath,
  buildFirmDocumentFilePath,
} from "../../domain/professionalContacts/professionalContacts.rules";

/**
 * Base URL resolution, in order:
 * 1. `PROFESSIONAL_CONTACTS_API_URL`, when set — an explicit override, e.g. to point at the
 *    Prism mock (`npx prism mock openapi/professional-contacts-openapi.json --port 4010`) during
 *    local dev. Note Prism serves the contract's paths at the bare host (no `/api/v1` prefix,
 *    even though the contract's `servers[].url` includes one).
 * 2. Otherwise, reuse the app's shared `API_URL` (the same host every other feature calls) with
 *    the contract's `/v1` version segment appended — the real Professional Contacts API is
 *    served from that same host, under `{API_URL}/v1` (matching `servers[].url`'s `/api/v1`,
 *    since `API_URL` already ends in `/api`).
 * 3. A last-resort fallback to the local Prism mock, for contexts where neither env var is set.
 */
const BASE_URL =
  process.env.PROFESSIONAL_CONTACTS_API_URL ||
  (process.env.API_URL && `${process.env.API_URL}/v1`) ||
  "http://127.0.0.1:4010";

const url = (path) => `${BASE_URL}${path}`;

const toQueryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

/** apis.js never rejects — failed calls resolve with the axios error object instead. Normalise that here. */
const toApiError = (result) => {
  const looksLikeFailure =
    result instanceof Error || (result && result.isAxiosError);
  if (!looksLikeFailure) return null;
  const body = result.response && result.response.data;
  const error = new Error(
    (body && body.message) || result.message || "Request failed"
  );
  error.status = result.response && result.response.status;
  error.code = body && body.code;
  error.fieldErrors = body && body.fieldErrors;
  return error;
};

/** @param {Promise<any>} pending */
async function request(pending) {
  const result = await pending;
  const error = toApiError(result);
  if (error) throw error;
  return result;
}

// ---------------------------------------------------------------------------------------
// Professional Contacts repository (DF154-168, DF176)
// ---------------------------------------------------------------------------------------

/**
 * @param {{type?: string, q?: string, active?: boolean, panel?: boolean, piiExpiring?: boolean, distressed?: boolean, noFurtherInstructions?: boolean, page?: number, pageSize?: number}} [params]
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").FirmSummaryPage>}
 */
export function listFirms(params) {
  return request(httpGet(url(`/professional-firms${toQueryString(params)}`)));
}

/** @param {import("../../domain/professionalContacts/professionalContacts.types").FirmUpsert} payload */
export function createFirm(payload) {
  return request(httpPost(url("/professional-firms"), payload));
}

/** @param {string} firmId */
export function getFirm(firmId) {
  return request(httpGet(url(`/professional-firms/${firmId}`)));
}

/**
 * @param {string} firmId
 * @param {import("../../domain/professionalContacts/professionalContacts.types").FirmUpsert} payload
 */
export function updateFirm(firmId, payload) {
  return request(httpPut(url(`/professional-firms/${firmId}`), payload));
}

/** @param {string} firmId */
export function deleteFirm(firmId) {
  return request(httpDelete(url(`/professional-firms/${firmId}`)));
}

/**
 * v1.13.0: is this company name already taken, and if so by which firm?
 *
 * Backs the Add/Edit company form's on-blur warning on Company Name. It is a CONVENIENCE, not
 * the enforcement point — the name can be taken between this call and the save, so createFirm/
 * updateFirm still answer 409 `duplicate_name`, which is the real authority.
 *
 * v1.14.0 (FIRM-DUPLICATE-NAMES-010/-012, R12): `type` (the FirmType WIRE value) is now
 * REQUIRED by the server — the answer is scoped to one firm type. `toQueryString` drops empty
 * values same as ever, so an omitted `type` here would silently produce the OLD call shape and
 * the server would answer with a 400, not the old repository-wide answer. That is the right
 * failure (loud, not silently wrong), but every caller must pass it.
 *
 * Pass `excludeFirmId` when editing so the firm does not collide with its own stored name; it
 * is dropped from the query string when absent (toQueryString skips empty values).
 *
 * `existingFirmName` comes back in the name's STORED casing, which is the whole point when the
 * only difference between it and what the user typed is case.
 *
 * @param {{name: string, type: string, excludeFirmId?: string}} params
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").FirmNameCheck>}
 */
export function checkFirmNameAvailable({ name, type, excludeFirmId } = {}) {
  return request(
    httpGet(
      url(
        `/professional-firms/name-available${toQueryString({
          name,
          type,
          excludeFirmId,
        })}`
      )
    )
  );
}

/**
 * @param {string} firmId
 * @param {string} contactId
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").Firm>} the updated firm
 */
export function deleteContact(firmId, contactId) {
  return request(
    httpDelete(url(`/professional-firms/${firmId}/contacts/${contactId}`))
  );
}

/**
 * @param {string} firmId
 * @param {string} addressId
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").Firm>} the updated firm
 */
export function deleteAddress(firmId, addressId) {
  return request(
    httpDelete(url(`/professional-firms/${firmId}/addresses/${addressId}`))
  );
}

/** @param {string} firmId */
export function getDeletedContacts(firmId) {
  return request(
    httpGet(url(`/professional-firms/${firmId}/deleted-contacts`))
  );
}

/** @param {string} firmId */
export function getDeletedAddresses(firmId) {
  return request(
    httpGet(url(`/professional-firms/${firmId}/deleted-addresses`))
  );
}

/**
 * @param {string} firmId
 * @param {string} bankAccountId
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").Firm>} the updated firm
 */
export function deleteBankAccount(firmId, bankAccountId) {
  return request(
    httpDelete(
      url(`/professional-firms/${firmId}/bank-accounts/${bankAccountId}`)
    )
  );
}

/** @param {string} firmId */
export function getDeletedBankAccounts(firmId) {
  return request(
    httpGet(url(`/professional-firms/${firmId}/deleted-bank-accounts`))
  );
}

/**
 * v1.12.0: server-side paginated/sorted/searched, mirroring listAssociatedLoans — the full list is
 * never fetched in one call; the Documents-section preview requests pageSize:5, the "View all"
 * modal (FIRM-DOCUMENTS-033) requests its own page.
 * @param {string} firmId
 * @param {{q?: string, sort?: string, sortDir?: "asc"|"desc", page?: number, pageSize?: number}} [params]
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").FirmDocumentPage>}
 */
export function listFirmDocuments(firmId, params) {
  return request(
    httpGet(url(`/professional-firms/${firmId}/documents${toQueryString(params)}`))
  );
}

/**
 * @param {string} firmId
 * @param {import("../../domain/professionalContacts/professionalContacts.types").FirmDocument} payload
 */
export function createFirmDocument(firmId, payload) {
  return request(
    httpPost(url(`/professional-firms/${firmId}/documents`), payload)
  );
}

/**
 * @param {string} firmId
 * @param {string} documentId
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").FirmDocument>}
 *   the deleted document — the API is deliberately self-sufficient here, so the caller has
 *   `filePath` for the blob delete without a second read
 */
export function deleteFirmDocument(firmId, documentId) {
  return request(
    httpDelete(url(`/professional-firms/${firmId}/documents/${documentId}`))
  );
}

/**
 * The firm's main bank account, shaped as just the 3 fields Outbound Payments locks for a
 * beneficiary (v1.3.0). Rejects with a 404 status if the firm has no bank account yet —
 * callers should treat that as "nothing to prefill", not a hard error.
 * @param {string} firmId
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").MainBankAccount>}
 */
export function getFirmMainBankAccount(firmId) {
  return request(
    httpGet(url(`/professional-firms/${firmId}/bank-accounts/main`))
  );
}

/**
 * Sort-code → bank lookup for the Bank Accounts editor (open decision #6, confirmed: match
 * existing precedent). Calls the BankFinder Azure Function directly from the browser via the
 * same helper AddPaymentDetails.helper.js already uses — not duplicated here. Reuses the same
 * `BANK_FINDER_API_URL` env var AddPaymentDetails.jsx already reads (the function URL, with its
 * key, is app config shared across both features — not a separate professional-contacts value).
 * @param {string} sortCode digits only, no dashes
 * @returns {Promise<{found: boolean, bankName?: string, town?: string, postcode?: string, country?: string, error?: string}>}
 */
export function lookupSortCode(sortCode) {
  const bankFinderUrl = process.env.BANK_FINDER_API_URL;
  // No configured function URL means there is no external call to make — same guard
  // AddPaymentDetails.jsx applies before its own lookup, rather than firing a doomed request.
  if (!bankFinderUrl) {
    return Promise.resolve({
      found: false,
      error: "Bank lookup is not configured",
    });
  }
  return lookupBankBySortCode(sortCode, bankFinderUrl);
}

/**
 * @param {string} firmId
 * @param {import("../../domain/professionalContacts/professionalContacts.types").FirmFlags} flags
 */
export function setFirmFlags(firmId, flags) {
  return request(httpPatch(url(`/professional-firms/${firmId}/flags`), flags));
}

/** @param {import("../../domain/professionalContacts/professionalContacts.types").FirmUpsert[]} rows */
export function bulkCreateFirms(rows) {
  return request(httpPost(url("/professional-firms/bulk"), rows));
}

/**
 * Bulk create from a raw CSV file's contents (the contract also accepts text/csv on this
 * endpoint) — the server does the parsing/validation, matching the flat-file workflow (§4a).
 * @param {string} csvText
 */
export function bulkCreateFirmsCsv(csvText) {
  return request(
    httpPost(url("/professional-firms/bulk"), csvText, {
      headers: { "Content-Type": "text/csv" },
    })
  );
}

export function listFirmTypes() {
  return request(httpGet(url("/reference/firm-types")));
}

export function listLoanPartyRoles() {
  return request(httpGet(url("/reference/loan-party-roles")));
}

/** @param {{firmId?: string, page?: number}} [params] */
export function listAudit(params) {
  return request(httpGet(url(`/audit/firms${toQueryString(params)}`)));
}

/**
 * Every loan this firm is currently assigned to as a party on that loan's Professional Details —
 * one row per (loan, role) pair (v1.5.0/v1.6.0). Server-side searched/sorted/paginated from day
 * one — a firm can be assigned on hundreds of loans, so this is the only place that volume is
 * ever fetched (the firm record's own preview also calls this, with a small pageSize).
 * @param {string} firmId
 * @param {{q?: string, status?: string, stage?: string, role?: string, sort?: string, sortDir?: "asc"|"desc", page?: number, pageSize?: number}} [params]
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").AssociatedLoanPage>}
 */
export function listAssociatedLoans(firmId, params) {
  return request(
    httpGet(url(`/professional-firms/${firmId}/loans${toQueryString(params)}`))
  );
}

// ---------------------------------------------------------------------------------------
// Loan Professional Details (DF169-177)
// ---------------------------------------------------------------------------------------

/** @param {string} loanId */
export function getLoanProfessionalDetails(loanId) {
  return request(httpGet(url(`/loans/${loanId}/professional-details`)));
}

/**
 * @param {string} loanId
 * @param {import("../../domain/professionalContacts/professionalContacts.types").LoanProfessionalDetails} payload
 */
export function saveLoanProfessionalDetails(loanId, payload) {
  return request(
    httpPut(url(`/loans/${loanId}/professional-details`), payload)
  );
}

/**
 * v1.9.0: add/amend the loan's Summary Notes. Independent of saveLoanProfessionalDetails above —
 * never touches a role assignment or quote, and is never affected by saving them.
 * @param {string} loanId
 * @param {string} notes
 */
export function saveLoanProfessionalNotes(loanId, notes) {
  return request(
    httpPut(url(`/loans/${loanId}/professional-details/notes`), { notes })
  );
}

/**
 * Firms selectable for a role on this loan — active and not "no further instructions" only
 * (DF171-175). Applies to every role, incl. Broker/BorrowerSolicitor/Administrator.
 * @param {string} loanId
 * @param {import("../../domain/professionalContacts/professionalContacts.types").LoanPartyRole} role
 */
export function getSelectableFirms(loanId, role) {
  return request(
    httpGet(
      url(
        `/loans/${loanId}/professional-details/selectable-firms${toQueryString({
          role,
        })}`
      )
    )
  );
}

/**
 * This loan's Professional Details roles that currently have a firm assigned, for the Outbound
 * Payments "Third Party" picker (Step A, v1.3.0) — NOT a repository-wide search.
 * @param {string} loanId
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").PayableParty[]>}
 */
export function getPayableParties(loanId) {
  return request(
    httpGet(url(`/loans/${loanId}/professional-details/payable-parties`))
  );
}

// ---------------------------------------------------------------------------------------
// Quote supporting documents — upload from the Add/Edit Quote row, download from the read view
// ---------------------------------------------------------------------------------------
// Note: unlike the calls above, uploadToDocumentHelper/downloadFromDocumentHelper carry their
// own bearer token and don't go through apis.js's shared axios config, so they need the caller
// to pass the oidc user explicitly, and they already reject with a real Error on failure — no
// request()/toApiError() wrapping needed here.

/**
 * Uploads a quote's supporting document to the same Loan Documents storage every other document
 * uses, filed under the fixed, system-generated "Quotes" category (QUOTE_DOCUMENT_CATEGORY_NAME)
 * so it appears under Loan Documents automatically with no separate sync step. The resulting
 * path is computed client-side rather than read from the Document Helper's response body — the
 * same convention DocumentsDashboard.jsx relies on (it re-lists the category instead of trusting
 * the upload response for the path).
 * @param {string} loanId
 * @param {File} file
 * @param {{access_token: string}} user the oidc user (state.oidc.user)
 * @returns {Promise<import("../../domain/professionalContacts/professionalContacts.types").QuoteDocument>}
 */
export async function uploadQuoteDocument(loanId, file, user) {
  await uploadToDocumentHelper(
    file,
    file.name,
    QUOTE_DOCUMENT_CATEGORY_NAME,
    loanId,
    user
  );
  return {
    fileName: file.name,
    filePath: buildQuoteDocumentFilePath(loanId, file.name),
    fileExtension: file.name.slice(file.name.lastIndexOf(".")).toLowerCase(),
    contentType: file.type || null,
    categoryName: QUOTE_DOCUMENT_CATEGORY_NAME,
    uploadedUtc: new Date().toISOString(),
  };
}

/**
 * Downloads a quote's supporting document via the existing Document Helper — the same "generic"
 * container every other Loan Document uses. "Quotes" is the category segment of the stored path
 * (document.filePath), not the Document Helper `type` header: that stays "generic", matching
 * every other Loan Document category (confirmed against DocumentsDashboard.helper.js — `type` is
 * a container discriminator, not a per-category value).
 * @param {import("../../domain/professionalContacts/professionalContacts.types").QuoteDocument} document
 * @param {{access_token: string}} user
 */
export function downloadQuoteDocument(document, user) {
  return downloadFromDocumentHelper(document.filePath, user, "generic");
}

// ---------------------------------------------------------------------------------------
// Firm documents (R5, R8, R13) — bytes go through the Document Helper, never through the API.
// ---------------------------------------------------------------------------------------

/**
 * Uploads a firm document's bytes at the same path (`buildFirmDocumentFilePath`) the caller will
 * register with `createFirmDocument` — the two must agree byte-for-byte (§2.3 rule 4), so the
 * folder is derived from that one computed path rather than reassembled from firmId/documentId
 * a second time.
 * @param {string} firmId
 * @param {string} documentId
 * @param {File} file
 * @param {{access_token: string}} user the oidc user (state.oidc.user)
 */
export function uploadFirmDocumentBlob(firmId, documentId, file, user) {
  const filePath = buildFirmDocumentFilePath(firmId, documentId, file.name);
  const folder = filePath.slice(0, filePath.length - file.name.length - 1);
  return uploadToDocumentHelper(file, file.name, null, folder, user);
}

/**
 * Downloads a firm document via the Document Helper. Sends the stored `filePath` verbatim —
 * never re-derives it from the document's other fields (R5).
 * @param {import("../../domain/professionalContacts/professionalContacts.types").FirmDocument} document
 * @param {{access_token: string}} user
 */
export function downloadFirmDocument(document, user) {
  return downloadFromDocumentHelper(document.filePath, user, "generic");
}

/**
 * Best-effort blob cleanup: called after a successful `deleteFirmDocument` (API first, blob
 * second — R8) or as compensating cleanup when a post-upload `createFirmDocument` 400s. Callers
 * are expected to swallow the rejection — a storage failure must never resurrect/block on a row
 * the API has already deleted, or block a dialog on a partially-failed upload.
 * @param {string} filePath
 * @param {{access_token: string}} user
 */
export function deleteFirmDocumentBlob(filePath, user) {
  return deleteFromDocumentHelper(filePath, user, "generic");
}
